import React, { useRef, useEffect } from 'react'
import usePrevious from '@rooks/use-previous'
import { FormattedMessage } from 'react-intl'
import cx from 'classnames'
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer'
import VirtualizedGrid from 'react-virtualized/dist/commonjs/MultiGrid'
import RowLoader from 'components/RowLoader'
import messages from './messages'
import { useWindowSize } from 'hooks/useWindowSize'
import styles from './Grid.scss'

export interface GridColumn {
  key: string
  size?: number
  minWidth?: number
  fixedWidth?: number
  headerClassName?: string
  columnClassName?: string
  groupHeader?: string
  firstInGroup?: boolean
  isSortable?: boolean
}

interface GridRow {
  id?: string | number
  [key: string]: any
}

interface GridProps {
  isLoading?: boolean
  withBoxShadow?: boolean
  rows: GridRow[]
  selectedRows?: (string | number)[]
  highlightedRow?: string | number
  fixedRowCount?: number
  fixedColumnCount?: number
  columns: GridColumn[]
  children: (item: GridRow, column: GridColumn, rowIndex: number, isHeader?: boolean) => React.ReactNode
  width?: number
  height?: number
  maxHeight?: number
  rowHeight?: number
  headerRowHeight?: number
  withGroupHeaders?: boolean
  withHeaders?: boolean
  onCellClick?: (columnIndex: number, item: GridRow) => void
  className?: string
  innerClassName?: string
  onHeaderClick?: (key: string) => void
}

interface CellRendererParams {
  key: string
  rowIndex: number
  columnIndex: number
  style: React.CSSProperties
}

export function Grid({
  isLoading = false,
  withBoxShadow = true,
  rows: items,
  selectedRows = [],
  highlightedRow,
  columns,
  fixedRowCount = 0,
  fixedColumnCount = 0,
  children: renderCell,
  rowHeight = 60,
  headerRowHeight = 40,
  width: fixedGridWidth,
  withGroupHeaders = false,
  withHeaders = false,
  onCellClick,
  className,
  innerClassName,
  onHeaderClick = () => {},
  maxHeight,
}: GridProps) {
  const { height: windowHeight } = useWindowSize()
  const height = windowHeight * 0.6

  const gridRef = useRef<VirtualizedGrid>(null)
  const previousColumns = usePrevious(columns)
  const previousFixedGridWidth = usePrevious(fixedGridWidth)

  const forceUpdate = () => {
    if (gridRef && gridRef.current) {
      gridRef.current.measureAllCells()
      gridRef.current.recomputeGridSize()
      gridRef.current.forceUpdateGrids()
    }
  }

  useEffect(() => {
    const areColumnsChanged = !!previousColumns && columns.length !== previousColumns.length
    if (areColumnsChanged) forceUpdate()
  }, [columns, previousColumns])

  useEffect(() => {
    const isFixedGridWidthChanged = !!previousFixedGridWidth && fixedGridWidth !== previousFixedGridWidth
    if (isFixedGridWidthChanged) forceUpdate()
  }, [fixedGridWidth, previousFixedGridWidth])

  let rows: GridRow[] = []
  if (withGroupHeaders) rows.push({})
  if (withHeaders) rows.push({})

  rows = [...rows, ...items]

  const overscanRowCount = 4

  const placeholder = () => (
    <div className={styles.noResults}>
      <FormattedMessage {...messages.noResults} />
    </div>
  )

  const cellRenderer = ({ key, rowIndex, columnIndex, style }: CellRendererParams) => {
    const currentItem = rows[rowIndex] || {}
    const { id: currentItemId = -1 } = currentItem

    const isSelected = selectedRows.includes(currentItemId)
    const isHighlighted = highlightedRow === currentItemId

    const currentColumn = columns[columnIndex]

    const isGroupHeaderCell = withGroupHeaders && rowIndex === 0
    const isHeaderCell = withHeaders && ((!withGroupHeaders && rowIndex === 0) || (withGroupHeaders && rowIndex === 1))
    const isContentCell = !isGroupHeaderCell && !isHeaderCell

    return (
      <span
        key={key}
        style={style}
        onClick={
          isHeaderCell
            ? columns[columnIndex]?.isSortable
              ? () => onHeaderClick(columns[columnIndex].key)
              : undefined
            : onCellClick
              ? () => onCellClick(columnIndex, currentItem)
              : undefined
        }
        className={cx(styles.cell, isHeaderCell ? currentColumn.headerClassName : currentColumn.columnClassName, {
          [styles.groupHeader]: isGroupHeaderCell && currentColumn.groupHeader,
          [styles.header]: isHeaderCell,
          [styles.content]: isContentCell,
          [styles.firstInGroup]: currentColumn.firstInGroup,
          [styles.selected]: isSelected,
          [styles.highlighted]: isHighlighted,
          [styles.separated]: !fixedRowCount,
          [styles.clickable]: (!isHeaderCell && !!onCellClick) || (isHeaderCell && columns[columnIndex]?.isSortable),
        })}
      >
        {isLoading && <RowLoader />}
        {!isLoading && (
          <>
            {isGroupHeaderCell && currentColumn.groupHeader && (
              <h4 className={styles.primaryTitle}>{currentColumn.groupHeader}</h4>
            )}
            {isHeaderCell && renderCell({}, columns[columnIndex], rowIndex, true)}
            {isContentCell && renderCell(rows[rowIndex], columns[columnIndex], rowIndex)}
          </>
        )}
      </span>
    )
  }

  const sumAllColumnsSizes = columns.reduce((acc, { size = 1 }) => acc + size, 0)

  const getColumnWidth = (columnIndex: number, gridWidth: number): number => {
    const currentColumn = columns[columnIndex]
    const { size = 1, minWidth = 100, fixedWidth } = currentColumn

    if (fixedWidth) return fixedWidth

    const relativeSize = (size / sumAllColumnsSizes) * gridWidth
    return Math.max(relativeSize, minWidth)
  }

  const getRowHeight = ({ index: rowIndex }: { index: number }): number => {
    const isGroupHeaderRow = withGroupHeaders && rowIndex === 0
    const isHeaderRow = withHeaders && ((!withGroupHeaders && rowIndex === 0) || (withGroupHeaders && rowIndex === 1))

    if (isGroupHeaderRow || isHeaderRow) return headerRowHeight
    return rowHeight
  }

  const withFixedColumns = fixedColumnCount > 0

  return (
    <div
      className={cx(styles.container, className, {
        [styles.noShadow]: !withBoxShadow,
      })}
    >
      <AutoSizer disableHeight>
        {({ width }) => (
          <VirtualizedGrid
            ref={gridRef}
            enableFixedRowScroll={!!fixedRowCount}
            enableFixedColumnScroll={withFixedColumns}
            fixedRowCount={fixedRowCount}
            fixedColumnCount={fixedColumnCount}
            hideTopRightGridScrollbar
            width={fixedGridWidth || width}
            height={Math.min(height, maxHeight || height)}
            noContentRenderer={placeholder}
            rowCount={rows.length}
            rowHeight={getRowHeight}
            overscanRowCount={overscanRowCount}
            overscanColumnCount={0}
            columnCount={columns.length}
            columnWidth={({ index }) => getColumnWidth(index, fixedGridWidth || width)}
            cellRenderer={cellRenderer}
            classNameBottomLeftGrid={styles.fixedColumns}
            classNameBottomRightGrid={cx(styles.contentGrid, innerClassName, {
              [styles.noScroll]: withFixedColumns,
            })}
            classNameTopLeftGrid={styles.fixedColumnsHeaders}
            classNameTopRightGrid={styles.headerRows}
          />
        )}
      </AutoSizer>
    </div>
  )
}

